// packages/web/src/components/marketplace/BuyBoxOption.tsx
// BuyBox option component — winner card + full secondary option cards
import { Avatar, Box, Button, Chip, Typography } from '@mui/material';
import React, { useState } from 'react';

import { BuyBoxOptionPerks } from './BuyBoxOptionPerks';
import { ServicePreviewModal } from '../service/ServicePreviewModal';

interface BuyBoxOptionProps {
  option: any;
  isWinner?: boolean;
  isFirst?: boolean;
  getPartnerDisplayName: (_option: any) => string;
  onSelect?: () => void;
}

const AVATAR_COLORS: Record<string, string> = {
  A: '#1565c0',
  B: '#6a1b9a',
  C: '#00695c',
  D: '#e65100',
  E: '#2e7d32',
  F: '#4527a0',
  G: '#00838f',
  H: '#c62828',
  I: '#558b2f',
  J: '#0277bd',
  K: '#6d4c41',
  L: '#ad1457',
  M: '#00897b',
  N: '#1976d2',
  O: '#7b1fa2',
  P: '#388e3c',
  Q: '#f57c00',
  R: '#d32f2f',
  S: '#0288d1',
  T: '#00695c',
  U: '#5e35b1',
  V: '#f57f17',
  W: '#455a64',
  X: '#bf360c',
  Y: '#827717',
  Z: '#37474f',
};

function getAvatarColor(name: string): string {
  const letter = (name || 'P').charAt(0).toUpperCase();
  return AVATAR_COLORS[letter] || '#3b82f6';
}

function getInitials(name: string): string {
  if (!name) return 'P';
  return name.charAt(0).toUpperCase();
}

function getIncludedServiceId(option: any): string | null {
  return (
    option?.included_service_id ||
    option?.metadata?.included_service_id ||
    option?.metadata?.bundled_service_id ||
    null
  );
}

function getSavingsCents(option: any): number | null {
  const candidates = [
    option?.savings_display_cents,
    option?.savings_cents,
    option?.metadata?.savings_display_cents,
    option?.metadata?.savings_cents,
  ];

  for (const value of candidates) {
    if (typeof value === 'number' && Number.isFinite(value) && value > 0) return value;
  }

  return null;
}

function getSavingsLabel(option: any): string | null {
  const savingsCents = getSavingsCents(option);
  if (savingsCents) return `Free · saves $${(savingsCents / 100).toLocaleString()}`;

  const fallback = option?.savings_display || option?.metadata?.savings_display;
  if (typeof fallback === 'string' && fallback.trim()) return `Free · ${fallback}`;

  return option?.perk_type === 'included_service' ? 'Free included service' : null;
}

function perkMeta(option: any): { label: string; chipBg: string; chipColor: string } | null {
  if (option.perk_type === 'rebate') {
    return { label: '🟩 Rebate', chipBg: '#dcfce7', chipColor: '#166534' };
  }
  if (option.perk_type === 'gift_card') {
    return { label: '🎁 Gift Card', chipBg: '#fef3c7', chipColor: '#92400e' };
  }
  if (option.perk_type === 'included_service') {
    return { label: '⚡ Included Service', chipBg: '#dbeafe', chipColor: '#1d4ed8' };
  }
  if (option.perk_type === 'free_service') {
    return { label: '⚡ Free Service', chipBg: '#dbeafe', chipColor: '#1d4ed8' };
  }
  if (option.perk_type) {
    return { label: option.perk_type, chipBg: '#eff6ff', chipColor: '#1d4ed8' };
  }
  return null;
}

export const BuyBoxOption: React.FC<BuyBoxOptionProps> = ({
  option,
  isWinner = true,
  isFirst = false,
  getPartnerDisplayName,
  onSelect,
}) => {
  const [showIncludedServiceDetails, setShowIncludedServiceDetails] = useState(false);

  const isBaseService = option.id === 'base';
  const partnerName = getPartnerDisplayName(option);
  const initials = getInitials(partnerName);
  const avatarColor = getAvatarColor(partnerName);
  const showFeaturedChip = option.is_featured || isFirst;
  const showVerifiedChip = Boolean(option.verified_badge_active);

  const perk = perkMeta(option);
  const includedServiceId = getIncludedServiceId(option);
  const isIncludedService =
    option.perk_type === 'included_service' || option.perk_type === 'free_service';
  const savingsLabel = getSavingsLabel(option);
  const perkTitle =
    option.perk_type === 'included_service'
      ? savingsLabel || option.name || option.perk_description || option.description
      : option.name || option.perk_description || option.description;
  const perkDescription = option.description || option.perk_description;

  if (isWinner) {
    return (
      <Box
        sx={{
          border: '2px solid #3b82f6',
          borderRadius: 2,
          p: 2,
          mb: 2,
          bgcolor: 'white',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: perk ? 1.25 : 0 }}>
          <Avatar
            sx={{
              width: 40,
              height: 40,
              bgcolor: avatarColor,
              color: 'white',
              fontWeight: 700,
              fontSize: '1rem',
              flexShrink: 0,
            }}
          >
            {initials}
          </Avatar>

          <Typography variant="subtitle1" fontWeight="bold" sx={{ flex: 1, minWidth: 0 }} noWrap>
            {partnerName}
          </Typography>

          {showVerifiedChip && (
            <Chip
              label="✓ Verified"
              size="small"
              sx={{
                backgroundColor: '#dbeafe',
                color: '#1d4ed8',
                fontSize: '0.7rem',
                height: 22,
                flexShrink: 0,
                mr: 0.5,
              }}
            />
          )}

          {showFeaturedChip && (
            <Chip
              icon={<span style={{ fontSize: '0.75rem' }}>★</span>}
              label="Exclusive incentive"
              size="small"
              sx={{
                backgroundColor: '#fef3c7',
                color: '#92400e',
                fontSize: '0.7rem',
                height: 22,
                flexShrink: 0,
                '& .MuiChip-icon': { marginLeft: '6px' },
              }}
            />
          )}
        </Box>

        {!isBaseService && perk && (
          <Box>
            <Chip
              label={perk.label}
              size="small"
              sx={{
                mb: 0.8,
                bgcolor: perk.chipBg,
                color: perk.chipColor,
                fontWeight: 700,
                fontSize: '0.72rem',
                height: 22,
              }}
            />
            {perkTitle && (
              <Typography variant="body2" sx={{ fontWeight: 700, color: '#1f2937', mb: 0.25 }}>
                {perkTitle}
              </Typography>
            )}
            {perkDescription && (
              <Typography variant="body2" sx={{ color: '#4b5563', lineHeight: 1.45 }}>
                {perkDescription}
              </Typography>
            )}

            {isIncludedService && includedServiceId && (
              <Box sx={{ mt: 0.5 }}>
                <ServicePreviewModal
                  serviceId={includedServiceId}
                  triggerLabel="See included service →"
                />
              </Box>
            )}

            {isIncludedService &&
              (option.name ||
                option.description ||
                option.perk_description ||
                includedServiceId) && (
                <Typography
                  component="span"
                  variant="body2"
                  onClick={() => setShowIncludedServiceDetails((v) => !v)}
                  sx={{
                    display: 'inline-block',
                    mt: 0.75,
                    color: '#3b82f6',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    cursor: 'pointer',
                    userSelect: 'none',
                  }}
                >
                  {showIncludedServiceDetails
                    ? '▲ Hide included service details'
                    : '▼ View included service details'}
                </Typography>
              )}

            {isIncludedService && showIncludedServiceDetails && (
              <BuyBoxOptionPerks option={option} isBaseService={isBaseService} />
            )}
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box
      onClick={onSelect}
      sx={{
        position: 'relative',
        p: 1.5,
        border: '1px solid #e5e7eb',
        borderRadius: 1.5,
        bgcolor: '#f9fafb',
        mb: 1,
        cursor: 'pointer',
        '&:hover': {
          borderColor: '#3b82f6',
          bgcolor: '#eff6ff',
        },
        transition: 'border-color 0.15s, background-color 0.15s',
      }}
    >
      <Button
        size="small"
        variant="outlined"
        onClick={(e) => {
          e.stopPropagation();
          onSelect?.();
        }}
        sx={{
          position: 'absolute',
          top: 10,
          right: 10,
          borderColor: '#3b82f6',
          color: '#3b82f6',
          textTransform: 'none',
          fontWeight: 600,
          lineHeight: 1,
          minHeight: 28,
        }}
      >
        Select
      </Button>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, pr: 9, mb: perk ? 1 : 0 }}>
        <Avatar
          sx={{
            width: 32,
            height: 32,
            bgcolor: avatarColor,
            color: 'white',
            fontWeight: 700,
            fontSize: '0.85rem',
            flexShrink: 0,
          }}
        >
          {initials}
        </Avatar>

        <Typography variant="body2" fontWeight={600} sx={{ minWidth: 0 }} noWrap>
          {partnerName}
        </Typography>

        {showVerifiedChip && (
          <Chip
            label="✓ Verified"
            size="small"
            sx={{
              fontSize: '0.68rem',
              height: 20,
              bgcolor: '#dbeafe',
              color: '#1d4ed8',
              flexShrink: 0,
            }}
          />
        )}
      </Box>

      {!isBaseService && perk && (
        <Box sx={{ pr: 9 }}>
          <Chip
            label={perk.label}
            size="small"
            sx={{
              mb: 0.65,
              bgcolor: perk.chipBg,
              color: perk.chipColor,
              fontWeight: 700,
              fontSize: '0.7rem',
              height: 20,
            }}
          />
          {perkTitle && (
            <Typography
              variant="body2"
              sx={{ fontWeight: 700, color: '#1f2937', lineHeight: 1.35 }}
            >
              {perkTitle}
            </Typography>
          )}
          {perkDescription && (
            <Typography variant="caption" sx={{ color: '#4b5563', lineHeight: 1.45 }}>
              {perkDescription}
            </Typography>
          )}
          {isIncludedService && includedServiceId && (
            <Box sx={{ mt: 0.5 }}>
              <ServicePreviewModal
                serviceId={includedServiceId}
                triggerLabel="See included service →"
              />
            </Box>
          )}
          {isIncludedService &&
            (option.name || option.description || option.perk_description || includedServiceId) && (
              <Typography
                component="span"
                variant="body2"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowIncludedServiceDetails((v) => !v);
                }}
                sx={{
                  display: 'inline-block',
                  mt: 0.75,
                  color: '#3b82f6',
                  fontSize: '0.8rem',
                  fontWeight: 500,
                  cursor: 'pointer',
                  userSelect: 'none',
                }}
              >
                {showIncludedServiceDetails
                  ? '▲ Hide included service details'
                  : '▼ View included service details'}
              </Typography>
            )}
          {isIncludedService && showIncludedServiceDetails && (
            <BuyBoxOptionPerks option={option} isBaseService={isBaseService} />
          )}
        </Box>
      )}
    </Box>
  );
};

export default BuyBoxOption;
